import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Globe, Send, LoaderCircle } from "lucide-react";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { nome: "", email: "", telefono: "", interesse: "Prodotti Propaq", messaggio: "" };

const Contatti = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato. Ti risponderemo al più presto.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova o scrivici via email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contatti-page" className="min-h-screen pt-20">
      <Seo
        title="Contatti | COMMPAQ — Propaq e Risorice"
        description="Contatta COMMPAQ per i prodotti gluten free Propaq o il franchising Risorice: scrivici e ti risponderemo al più presto."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="flex flex-col justify-between bg-forest px-6 py-20 text-bone lg:min-h-[calc(100vh-5rem)] lg:px-16 lg:py-24">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-sage">Contatti</p>
            <h1
              data-testid="contatti-heading"
              className="mt-8 font-display text-5xl font-medium leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl"
            >
              <MaskedLine delay={0.15}>Parliamone</MaskedLine>
              <MaskedLine delay={0.3} className="italic text-ochre">
                di persona.
              </MaskedLine>
            </h1>
            <FadeUp delay={0.6}>
              <p className="mt-10 max-w-md leading-relaxed text-bone/70">
                Rivenditore interessato ai prodotti Propaq? Imprenditore curioso del franchising
                Risorice? Scrivici: ogni buona collaborazione inizia da una conversazione.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.8} className="mt-16 space-y-6">
            <a
              href="mailto:info@commpaq.it"
              data-testid="contatti-email-link"
              className="flex items-center gap-4 text-lg text-bone/80 transition-colors duration-300 hover:text-ochre"
            >
              <Mail size={20} className="text-ochre" />
              info@commpaq.it
            </a>
            <a
              href="https://www.piattiprontiglutenfree.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contatti-propaq-site-link"
              className="flex items-center gap-4 text-bone/60 transition-colors duration-300 hover:text-ochre"
            >
              <Globe size={20} className="text-ochre" />
              www.piattiprontiglutenfree.com
            </a>
          </FadeUp>
        </div>

        <div className="flex items-center px-6 py-20 lg:px-16 lg:py-24">
          <FadeUp delay={0.3} className="w-full">
            <form data-testid="contact-form" onSubmit={handleSubmit} className="w-full space-y-10">
              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label htmlFor="nome" className="text-xs uppercase tracking-[0.2em] text-forest/60">
                    Nome e cognome *
                  </label>
                  <Input
                    id="nome"
                    data-testid="contact-name-input"
                    required
                    value={form.nome}
                    onChange={update("nome")}
                    placeholder="Mario Rossi"
                    className="mt-3 rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-forest/60">
                    Email *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="mario@esempio.it"
                    className="mt-3 rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label htmlFor="telefono" className="text-xs uppercase tracking-[0.2em] text-forest/60">
                    Telefono
                  </label>
                  <Input
                    id="telefono"
                    type="tel"
                    data-testid="contact-phone-input"
                    value={form.telefono}
                    onChange={update("telefono")}
                    placeholder="+39 ..."
                    className="mt-3 rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0"
                  />
                </div>
                <div>
                  <label htmlFor="interesse" className="text-xs uppercase tracking-[0.2em] text-forest/60">
                    Sono interessato a
                  </label>
                  <select
                    id="interesse"
                    data-testid="contact-interest-select"
                    value={form.interesse}
                    onChange={update("interesse")}
                    className="mt-3 w-full rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base text-forest focus:border-moss focus:outline-none"
                  >
                    <option>Prodotti Propaq</option>
                    <option>Franchising Risorice</option>
                    <option>Altro</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="messaggio" className="text-xs uppercase tracking-[0.2em] text-forest/60">
                  Messaggio *
                </label>
                <Textarea
                  id="messaggio"
                  data-testid="contact-message-input"
                  required
                  value={form.messaggio}
                  onChange={update("messaggio")}
                  placeholder="Raccontaci la tua idea o la tua richiesta..."
                  rows={5}
                  className="mt-3 resize-none rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0"
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="group inline-flex items-center gap-3 rounded-full bg-moss px-10 py-7 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-forest hover:shadow-xl hover:shadow-moss/30 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    Invio in corso
                    <LoaderCircle size={16} className="animate-spin" />
                  </>
                ) : (
                  <>
                    Invia messaggio
                    <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </FadeUp>
        </div>
      </div>
    </div>
  );
};

export default Contatti;
